/**
 * FAQ content builder (#253).
 *
 * Combines evergreen answers with data-driven ones woven from the live `/api/faq`
 * aggregates (tracking coverage, caliber count, cheapest in-stock listing per
 * top caliber). The same entries drive both the visible page and the FAQPage
 * JSON-LD, so the structured-data answers always match what's on screen (a
 * Google requirement) — the page must render these strings verbatim.
 */

import type { FaqEntry } from './seo/jsonLd';
import type { FaqData } from '../services/faqApi';

const EVERGREEN: FaqEntry[] = [
  {
    question: 'What is Round Recon and how does it work?',
    answer:
      'Round Recon is an ammunition search engine. We continuously aggregate listings from many retailers, normalize the inconsistent product data, and let you search and filter by caliber, brand, bullet type, price per round, and availability — then alert you when your criteria are met.',
  },
  {
    question: 'Does Round Recon sell ammunition?',
    answer:
      'No. Round Recon does not sell ammunition or process payments. We link out to the retailers, who handle the purchase, payment, shipping, and any age or eligibility checks.',
  },
  {
    question: 'How is price per round (CPR) calculated?',
    answer:
      'Price per round is a listing’s total price divided by its round count. It’s the fairest way to compare ammo across different pack sizes, so Round Recon ranks and sorts results by it.',
  },
  {
    question: 'How fresh is your pricing data?',
    answer:
      'Every listing carries a freshness state — fresh, aging, or stale — and a last-updated time, shown right on the listing. Prices and stock change fast, so always confirm the final price on the retailer’s own site before buying.',
  },
  {
    question: 'How is retailer trust scored?',
    answer:
      'Each retailer has a trust score based on data reliability and history. Trust feeds into result ranking alongside price per round, availability, and freshness.',
  },
  {
    question: 'Are sponsored listings labeled?',
    answer:
      'Yes. Sponsored and promoted listings are always clearly labeled — both visually and in the page markup — and are never silently mixed in as organic results.',
  },
];

/**
 * Build the full FAQ entry list. Returns the evergreen Q&A alone when no live
 * data is available, then appends data-driven entries.
 *
 * @param data live aggregates from `GET /api/faq` (optional)
 */
export function buildFaqEntries(data?: FaqData): FaqEntry[] {
  const entries: FaqEntry[] = [...EVERGREEN];
  if (!data) return entries;

  if (data.inStockListings > 0 || data.activeRetailers > 0) {
    entries.push({
      question: 'How many retailers and listings does Round Recon track?',
      answer: `Right now Round Recon tracks ${data.inStockListings.toLocaleString()} in-stock listings across ${data.activeRetailers.toLocaleString()} active retailers. These numbers update continuously as we re-check inventory.`,
    });
  }

  if (data.caliberCount > 0) {
    entries.push({
      question: 'What calibers can I track on Round Recon?',
      answer: `Round Recon covers ${data.caliberCount} calibers — from common handgun and rifle rounds like 9mm Luger, .223 Remington, and .308 Winchester to rimfire and shotgun. Search any caliber to see in-stock listings ranked by price per round.`,
    });
  }

  for (const d of data.cheapestByCaliber) {
    const atRetailer = d.retailer ? ` at ${d.retailer}` : '';
    entries.push({
      question: `What is the cheapest ${d.caliber} ammo right now?`,
      answer: `The lowest in-stock ${d.caliber} on Round Recon right now is $${d.pricePerRound.toFixed(3)} per round${atRetailer}. Prices change continuously — see all current ${d.caliber} deals on the ${d.caliber} page.`,
    });
  }

  return entries;
}
